import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, Menu, Truck, ChevronDown, Home as HomeIcon, Newspaper, Zap } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { Logo } from "@/components/store/Logo";
import { SPECIES, SPECIES_CATEGORIES, catLabel } from "@/lib/taxonomy";

const LINKS = [
  { to: "/", label: "Inicio" },
  { to: "/tienda", label: "Tienda" },
  { to: "/blog", label: "Blog" },
  { to: "/como-comprar", label: "Cómo comprar" },
  { to: "/nosotros", label: "Nosotros" },
];

export const Navbar = () => {
  const { count, setOpen } = useCart();
  const { settings } = useSettings();
  const [menuOpen, setMenuOpen] = useState(false);
  const [expSpecies, setExpSpecies] = useState(null);
  const navigate = useNavigate();

  const goTo = (path) => { setMenuOpen(false); navigate(path); };

  return (
    <>
      {/* announcement bar */}
      <div className="bg-charcoal text-cream text-[11px] sm:text-xs">
        <div className="max-w-6xl mx-auto px-4 py-1.5 flex items-center justify-center gap-4 text-center">
          <span className="inline-flex items-center gap-1.5"><Truck className="w-3.5 h-3.5 text-teal" /> Envíos a todo Costa Rica 🇨🇷</span>
          <span className="inline-flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-teal" /> Compras rápidas sin registro ni tarjeta</span>
          <span className="hidden sm:inline-flex items-center gap-1.5">🐾 Donamos 10% de utilidades a casas cuna</span>
        </div>
      </div>

      <header className="sticky top-0 z-30 bg-cream/90 backdrop-blur-md border-b border-[#E3E7E2]">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          {/* mobile menu */}
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-button" className="md:hidden grid place-items-center w-10 h-10 rounded-2xl bg-white border border-[#E3E7E2] text-charcoal" aria-label="Menú">
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-80 max-w-[85vw] bg-cream border-r border-[#E3E7E2] p-0 flex flex-col">
              <div className="p-5 border-b border-[#E3E7E2] flex items-center gap-2">
                <Logo size={36} />
                <span className="font-display text-lg font-bold text-charcoal">{settings.store_name}</span>
              </div>
              <div className="flex-1 overflow-y-auto p-3">
                <button onClick={() => goTo("/")} data-testid="menu-link-Inicio" className="w-full text-left px-4 py-3 rounded-2xl font-semibold text-sm text-charcoal hover:bg-peach/50 inline-flex items-center gap-2"><HomeIcon className="w-4 h-4" /> Inicio</button>

                <p className="px-4 pt-4 pb-1 text-[11px] font-bold uppercase tracking-wider text-muted-moss">Comprar por especie</p>
                {SPECIES.map((s) => (
                  <div key={s.slug} className="rounded-2xl overflow-hidden">
                    <button
                      data-testid={`menu-species-${s.slug}`}
                      onClick={() => setExpSpecies(expSpecies === s.slug ? null : s.slug)}
                      className="w-full flex items-center gap-2.5 px-4 py-3 font-semibold text-sm text-charcoal hover:bg-peach/50 rounded-2xl"
                    >
                      <s.icon className="w-4 h-4 text-coral" />
                      <span className="flex-1 text-left">{s.name}</span>
                      <ChevronDown className={`w-4 h-4 text-muted-moss transition-transform ${expSpecies === s.slug ? "rotate-180" : ""}`} />
                    </button>
                    {expSpecies === s.slug && (
                      <div className="pl-9 pr-2 pb-2 flex flex-col animate-float-up">
                        <button onClick={() => goTo(`/tienda?especie=${s.slug}`)} className="text-left px-3 py-2 rounded-xl text-sm text-coral font-semibold hover:bg-sage/40">Ver todo</button>
                        {(SPECIES_CATEGORIES[s.slug] || []).map((c) => (
                          <button key={c} data-testid={`menu-cat-${s.slug}-${c}`} onClick={() => goTo(`/tienda?especie=${s.slug}&categoria=${c}`)} className="text-left px-3 py-2 rounded-xl text-sm text-charcoal/80 hover:bg-sage/40">{catLabel(c)}</button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                <p className="px-4 pt-4 pb-1 text-[11px] font-bold uppercase tracking-wider text-muted-moss">Más</p>
                <button onClick={() => goTo("/blog")} className="w-full text-left px-4 py-3 rounded-2xl font-semibold text-sm text-charcoal hover:bg-peach/50 inline-flex items-center gap-2"><Newspaper className="w-4 h-4" /> Blog</button>
                <button onClick={() => goTo("/como-comprar")} className="w-full text-left px-4 py-3 rounded-2xl font-semibold text-sm text-charcoal hover:bg-peach/50 inline-flex items-center gap-2"><Truck className="w-4 h-4" /> Cómo comprar</button>
                <button onClick={() => goTo("/nosotros")} data-testid="menu-link-Nosotros" className="w-full text-left px-4 py-3 rounded-2xl font-semibold text-sm text-charcoal hover:bg-peach/50 inline-flex items-center gap-2">🐾 Nosotros</button>
              </div>
            </SheetContent>
          </Sheet>

          <Link to="/" data-testid="store-logo-link" className="flex items-center gap-2 group">
            <Logo size={40} className="group-hover:scale-105 transition-transform" />
            <span className="font-display text-xl font-bold text-charcoal leading-none hidden sm:block">
              {settings.store_name}
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1 ml-4">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label}`}
                className={({ isActive }) => `px-3.5 py-2 rounded-2xl text-sm font-semibold transition-colors ${isActive ? "text-coral" : "text-charcoal hover:bg-peach/50"}`}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex-1" />

          <button
            data-testid="search-open-button"
            onClick={() => navigate("/tienda")}
            className="grid place-items-center w-10 h-10 rounded-2xl bg-white border border-[#E3E7E2] text-charcoal hover:bg-sage/40 transition-colors"
            aria-label="Buscar"
          >
            <Search className="w-5 h-5" />
          </button>

          <Link to="/como-comprar" data-testid="nav-help-link" className="hidden md:grid place-items-center w-10 h-10 rounded-2xl bg-white border border-[#E3E7E2] text-charcoal hover:bg-sage/40 transition-colors" aria-label="Cómo comprar">
            <Truck className="w-5 h-5" />
          </Link>

          <button
            data-testid="cart-open-button"
            onClick={() => setOpen(true)}
            className="relative grid place-items-center w-10 h-10 rounded-2xl bg-coral text-white card-shadow hover:bg-coral-dark transition-colors"
            aria-label="Carrito"
          >
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && (
              <span data-testid="cart-item-count" className="absolute -top-1.5 -right-1.5 min-w-5 h-5 px-1 grid place-items-center rounded-full bg-teal text-charcoal text-[11px] font-bold animate-pop">
                {count}
              </span>
            )}
          </button>
        </div>
      </header>
    </>
  );
};
